import { makeAutoObservable, runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";

import { APPS, LABS } from "../../../../../../constants/AppsConstants.ts";
import { ImageEditingFeatureDetector } from "../../../../../../constants/ImageEditingFeatures.ts";
import RouterConstants from "../../../../../../constants/RouterConstants.ts";
import { IMAGE_EDITING_FEATURES } from "../../../../../../constants/routesMapper.ts";
import { BrushActions } from "../../../../../../hooks/brush/brushActions.ts";
import { IRootStore } from "../../../../../../mobx/root-store.tsx";
import { BrushCanvasRefs, IBrushSettings } from "../../../../../../models/brush.ts";
import { ImageToImageConfigType, PlaygroundImage, PlaygroundResult } from "../../../../../../models/image-to-image.ts";
import { IframePostMessageActionTypes } from "../../../../../../models/new-iframe.ts";
import QueryService from "../../../../../../utils/QueryService.ts";
import iframeStore from "../../../../../IframeNew/iframe-store.tsx";
import { DEFAULT_BRUSH, OBJECT_MANIPULATION_CONFIGS, SegmentationMaskTypes } from "./eraseObjectConstants.ts";
import { ObjectManipulationType } from "./eraseObjectTypes.ts";

export interface IEditingCanvasStore {
	rootStore: IRootStore;
	brushCanvasRefs: BrushCanvasRefs[];
	brushConfigs: IBrushSettings;
	objectManipulationConfig: ObjectManipulationType;
	isErasingImages: boolean;
	thumbnailNavigationSlideToImage: PlaygroundImage | null;
	isMaskApplied: boolean;
	isEraseConfigsEnabled: boolean;
	isDrawing: boolean;

	handleConfigChange: <K extends keyof ObjectManipulationType>(key: K, value: ObjectManipulationType[K]) => void;
	toggleIsMaskApplied: (status: boolean) => void;
	toggleEraseConfigs: () => void;
	handleResetBrusher: () => void;
	getMasks: (imageUrl: string) => Promise<string>;
	eraseObjectV2: (image: PlaygroundImage, config?: ImageToImageConfigType) => Promise<void>;
	callGenFillApiV2: (image: PlaygroundImage, config?: ImageToImageConfigType) => Promise<void>;
	resetObjectManipulationConfig: () => void;
}

export default class EditingCanvasStore implements IEditingCanvasStore {
	private canvasEditingService: QueryService = new QueryService("/canvas_editing");
	private abortController: AbortController = new AbortController();

	rootStore: IRootStore;
	isMaskApplied: boolean = false;
	objectManipulationConfig: ObjectManipulationType = OBJECT_MANIPULATION_CONFIGS;
	isErasingImages: boolean = false;
	thumbnailNavigationSlideToImage: PlaygroundImage | null = null;
	isEraseConfigsEnabled: boolean = false;
	isDrawing: boolean = false;
	brushCanvasRefs: BrushCanvasRefs[] = [];
	brushConfigs: IBrushSettings = {
		...DEFAULT_BRUSH,
		isMaskApplied: false,
	};

	constructor(rootStore: IRootStore) {
		this.rootStore = rootStore;
		makeAutoObservable(this);
	}

	// Prepare the request body for object manipulation APIs
	private prepareMaskBasedRequestsBody = async (
		imageUrl: string,
		maskType: "manual" | "automatic",
		isGenFill?: boolean, // Currently used just in gen_fill_v2 maybe change the naming
	): Promise<{ body: FormData; maskFileBase64: string }> => {
		if (!this.brushCanvasRefs.length || !this.brushCanvasRefs[0].canvasRef)
			throw new Error("Brush canvas refs not found");

		const brushActions = new BrushActions(
			this.brushCanvasRefs[0].canvasRef.current,
			this.brushCanvasRefs[0].canvasOverlayRef.current,
		);
		const maskFileBase64 = await brushActions.handleCanvasDownload();
		if (!maskFileBase64 || !imageUrl) throw new Error("Mask file base64 or image url not found");

		try {
			const formData = new FormData();
			formData.append("image", imageUrl);
			formData.append("mask", maskFileBase64);
			formData.append("mask_type", maskType);
			formData.append(
				"visual_input_content_moderation",
				String(this.rootStore.imageToImageStore.config.content_moderation),
			);
			formData.append(
				"visual_output_content_moderation",
				String(this.rootStore.imageToImageStore.config.content_moderation),
			);
			formData.append(
				"prompt_content_moderation",
				String(this.rootStore.imageToImageStore.config.content_moderation),
			);
			this.objectManipulationConfig = {
				...this.objectManipulationConfig,
				mask: maskFileBase64,
			};

			if (isGenFill) {
				// Using just in gen_fill
				const img = new Image();
				img.src = imageUrl;

				await new Promise<void>((resolve, reject) => {
					img.onload = () => {
						formData.append("mask_width", String(img.width)); // To fit the aspect ratio
						formData.append("mask_height", String(img.height)); // To fit the aspect ratio
						formData.append("prompt", this.objectManipulationConfig.prompt || "");
						formData.append("num_results", String(this.objectManipulationConfig.num_results || 0));
						this.objectManipulationConfig.tailored_model_id &&
							formData.append(
								"tailored_model_id",
								String(this.objectManipulationConfig.tailored_model_id),
							);
						this.objectManipulationConfig.tailored_model_version &&
							formData.append(
								"tailored_model_version",
								String(this.objectManipulationConfig.tailored_model_version),
							);
						resolve();
					};
					img.onerror = (err) => reject(new Error(`Failed to load image: ${err}`));
				});
			}

			return { body: formData, maskFileBase64 };
		} catch (e) {
			throw new Error(`Error erasing object ${e}`);
		}
	};

	showLoadingSkeletons = (
		config: ImageToImageConfigType,
		numResults: number = 1,
		selected: boolean = false,
		type?: APPS | LABS,
	): void => {
		console.log("showing loading skeletons", numResults, selected, type);

		const resultsSkeletons: PlaygroundResult = {
			id: uuidv4(),
			config,
			type:
				type ||
				(location.pathname.includes(RouterConstants.AUTOMOTIVE.path) ? APPS.AUTOMOTIVE : APPS.IMAGE_TO_IMAGE),
			images: Array.from({ length: numResults }).map((_) => ({
				id: "",
				url: "",
				playground_result_id: "",
				loading: true,
				selected,
			})),
		};

		this.rootStore.playgroundStore.playgroundResults = [
			...this.rootStore.playgroundStore.playgroundResults,
			resultsSkeletons,
		];
	};

	/**
	 * Adds new single image to a given playground result
	 *
	 * @param image The playground image to add
	 * @param newImageUrl The new URL of the generated
	 * @param savedPlaygroundResult The playground result to add the image to
	 * @returns The added image
	 */
	addNewSingleImage = (
		image: PlaygroundImage,
		newImageUrl: string,
		savedPlaygroundResult: PlaygroundResult,
	): PlaygroundImage => {
		if (image.file) delete image.file;
		const newImage: PlaygroundImage = {
			...image,
			id: uuidv4(),
			url: newImageUrl,
			loading: true,
			type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path) ? APPS.AUTOMOTIVE : APPS.IMAGE_TO_IMAGE,
			config: {
				...savedPlaygroundResult.config,
				original_image: { ...image },
				size: {},
			} as ImageToImageConfigType,
			image_config: {
				object_manipulation: {
					...this.objectManipulationConfig,
					erase: true,
				},
			} as ImageToImageConfigType,
			mode: "single",
		};

		savedPlaygroundResult.images = [newImage];
		return newImage;
	};

	eraseObjectV2 = async (
		image: PlaygroundImage,
		config: ImageToImageConfigType = this.rootStore.imageToImageStore.config,
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const savedIndex = playgroundStore.playgroundResults.length;
		this.isErasingImages = true;

		this.showLoadingSkeletons(config, 1, true);

		try {
			const maskType = this.objectManipulationConfig.mask_type || SegmentationMaskTypes.MANUAL;
			const { body, maskFileBase64 } = await this.prepareMaskBasedRequestsBody(
				image.url,
				maskType as "manual" | "automatic",
			);
			const res = await this.canvasEditingService.post("/eraser_v2", body, {
				"Content-Type": "multipart/form-data",
			});

			if (!res?.image_url) {
				playgroundStore.removeNotLoadingImages(); // What is it?? (maybe change the naming)
			} else if (res) {
				const { image_url: newImageUrl } = res;
				const { playgroundResults, projectsStore } = playgroundStore;
				const savedPlaygroundResult = playgroundResults[savedIndex];

				const newImage = this.addNewSingleImage(image, newImageUrl, savedPlaygroundResult);
				playgroundStore.handleSelectSingleImage(newImage); // Select the new image

				iframeStore.sendImageEditingPostMessage({
					action: IframePostMessageActionTypes.EraseObject,
					feature: ImageEditingFeatureDetector.getFeatureDisplayNames()[IMAGE_EDITING_FEATURES.ERASE_OBJECT],
					inputImage: image,
					resultUrls: [newImageUrl],
					config: {
						...config,
						object_manipulation: { ...this.objectManipulationConfig, erase: true, selected: true },
					},
					maskSrc: maskFileBase64,
				});

				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}
			} else {
				// What is the difference between this and `removeNotLoadingImages`?
				playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
					(_, index) => index !== savedIndex,
				);
				throw new Error(`Error erasing object`);
			}
		} catch (e: any) {
			if (e?.message.includes("422")) {
				this.thumbnailNavigationSlideToImage = image;
			}

			this.rootStore.imageToImageStore.showAPIError(e);
			playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
				(_, index) => index !== savedIndex,
			);
			throw new Error(`Error erasing object: ${e.message || e.toString()}`);
		} finally {
			this.isErasingImages = false;
			this.toggleIsMaskApplied(false);
		}
	};

	callGenFillApiV2 = async (
		image: PlaygroundImage,
		config: ImageToImageConfigType = this.rootStore.imageToImageStore.config,
	): Promise<void> => {
		const { playgroundStore } = this.rootStore;
		const { projectsStore } = playgroundStore;
		const selectedImage: PlaygroundImage = image;
		const savedIndex = playgroundStore.playgroundResults.length;
		this.abortController = new AbortController();
		this.isErasingImages = true;

		this.showLoadingSkeletons(config, this.objectManipulationConfig?.num_results || 0);

		try {
			const maskType = this.objectManipulationConfig?.mask_type || SegmentationMaskTypes.MANUAL;
			const { body, maskFileBase64 } = await this.prepareMaskBasedRequestsBody(selectedImage.url, maskType, true);

			const { images_urls: newImagesUrls } = await this.canvasEditingService.post("/gen_fill_v2", body, {
				"Content-Type": "multipart/form-data",
			});

			if (!newImagesUrls || newImagesUrls!.length === 0) {
				this.rootStore.playgroundStore.removeNotLoadingImages();
				return;
			}

			runInAction(() => {
				const startIndex = savedIndex * (this.objectManipulationConfig?.num_results || 0);
				newImagesUrls.forEach((generatedImageUrl: string, resultIndex: number) => {
					const finalIndex = startIndex + resultIndex;
					const newImage = {
						...selectedImage,
						...(selectedImage.file ? { file: undefined } : {}),
						id: uuidv4(),
						url: generatedImageUrl,
						loading: true,
						type: location.pathname.includes(RouterConstants.AUTOMOTIVE.path)
							? APPS.AUTOMOTIVE
							: APPS.IMAGE_TO_IMAGE,
						config: {
							...config,
							original_image: { ...selectedImage },
							size: {},
						} as ImageToImageConfigType,
						image_config: {
							object_manipulation: {
								...this.objectManipulationConfig,
								gen_fill: true,
							},
						} as ImageToImageConfigType,
						mode: "single",
					};

					const availableIndex = playgroundStore.playgroundResults[savedIndex].images.findIndex(
						(img) => img.loading && img.id === "" && img.url === "",
					);

					const targetIndex = availableIndex !== -1 ? availableIndex : finalIndex;
					playgroundStore.handleSelectSingleImage(newImage);
					playgroundStore.playgroundResults[savedIndex].images[targetIndex] = newImage;

					iframeStore.sendImageEditingPostMessage({
						action: IframePostMessageActionTypes.GenerativeFill,
						feature:
							ImageEditingFeatureDetector.getFeatureDisplayNames()[
								IMAGE_EDITING_FEATURES.GENERATIVE_FILL
							],
						inputImage: selectedImage,
						resultUrls: [generatedImageUrl],
						config: {
							...config,
							object_manipulation: { ...this.objectManipulationConfig, gen_fill: true, selected: true },
						},
						maskSrc: maskFileBase64,
					});
				});

				if (playgroundStore.selectedConfig === APPS.AUTOMOTIVE) {
					projectsStore.createPlaygroundResult(playgroundStore.playgroundResults[savedIndex]);
				}
			});
		} catch (e: any) {
			if (e?.message.includes("422")) {
				this.thumbnailNavigationSlideToImage = selectedImage;
				this.rootStore.imageToImageStore.errors.generate = {
					message: e?.message ?? "",
					status: 422,
				};
			}

			playgroundStore.playgroundResults = playgroundStore.playgroundResults.filter(
				(_, index) => index !== savedIndex,
			);
			throw new Error(`Error generating images: ${e.message || e.toString()}`);
		} finally {
			this.isErasingImages = false;
			this.toggleIsMaskApplied(false);
		}
	};

	getMasks = async (imageUrl: string) => {
		try {
			const formData = new FormData();
			formData.append("image_url", imageUrl);

			const result = await this.canvasEditingService.post(
				"/get_masks",
				formData,
				{ "Content-Type": "multipart/form-data" },
				{ signal: this.abortController.signal },
			);

			return result?.objects_masks;
		} catch (error) {
			console.log(error);
			this.rootStore.imageToImageStore.showAPIError(error);
		}
	};

	handleResetBrusher = () => {
		if (this.brushCanvasRefs.length > 0) {
			const canvasRef = this.brushCanvasRefs[0]?.canvasRef.current;
			const overlayCanvasRef = this.brushCanvasRefs[0]?.canvasOverlayRef.current;

			if (overlayCanvasRef) {
				const brushActions = new BrushActions(canvasRef, overlayCanvasRef);

				// Clear the canvas
				brushActions.handleCanvasClear();

				// Update the reset flag in the store
				runInAction(() => {
					this.brushConfigs.reset = false;
				});
			}
		}
	};

	toggleIsMaskApplied = (status: boolean) => {
		runInAction(() => {
			this.isMaskApplied = status;
		});
	};

	handleConfigChange = <K extends keyof ObjectManipulationType>(key: K, value: ObjectManipulationType[K]) => {
		runInAction(() => {
			this.objectManipulationConfig[key] = value;
		});
	};

	resetObjectManipulationConfig = () => {
		this.objectManipulationConfig = OBJECT_MANIPULATION_CONFIGS;
	};

	toggleEraseConfigs = () => {
		runInAction(() => {
			this.isEraseConfigsEnabled = !this.isEraseConfigsEnabled;
		});
	};
}
